import React, { Component } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { injectIntl, intlShape, FormattedMessage } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import { isPriceVariationsEnabled } from '../../../util/configHelpers';
import { isAuthenticatedSelector } from '../../../ducks/auth.duck';
import { getPriceDisplayValue } from '../../../util/priceVisibility';

import { PriceDisplay } from '../../../components';
import css from './SearchMapPriceLabel.module.css';

/**
 * SearchMapPriceLabel component
 * TODO: change to functional component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {propTypes.listing} props.listing - The listing
 * @param {Function} props.onListingClicked - The function to handle the listing click
 * @param {Object} props.config - The configuration
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
class SearchMapPriceLabel extends Component {
  shouldComponentUpdate(nextProps) {
    const currentListing = ensureListing(this.props.listing);
    const nextListing = ensureListing(nextProps.listing);
    const isSameListing = currentListing.id.uuid === nextListing.id.uuid;
    const hasSamePrice = currentListing.attributes.price === nextListing.attributes.price;
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;
    const hasSameAuth = this.props.isAuthenticated === nextProps.isAuthenticated;

    return !(
      isSameListing &&
      hasSamePrice &&
      hasSameActiveStatus &&
      hasSameRefreshToken &&
      hasSameAuth
    );
  }

  render() {
    const {
      className,
      rootClassName,
      listing,
      onListingClicked,
      intl,
      isActive,
      config,
      isAuthenticated,
    } = this.props;
    const currentListing = ensureListing(listing);
    const { price, publicData, title } = currentListing.attributes;

    // Create formatted price if currency is known or alternatively show just the unknown currency.
    const formattedPrice =
      price && price.currency === config.currency
        ? formatMoney(intl, price)
        : price?.currency
        ? price.currency
        : null;

    const priceValueNode = formattedPrice ? (
      <PriceDisplay value={formattedPrice} />
    ) : null;

    const validListingTypes = config.listing.listingTypes;
    const foundListingTypeConfig = validListingTypes.find(
      conf => conf.listingType === publicData?.listingType
    );
    const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
    const hasMultiplePriceVariants =
      isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

    const priceMessage = hasMultiplePriceVariants ? (
      <FormattedMessage
        id="SearchMapInfoCard.priceStartingFrom"
        values={{ priceValue: priceValueNode }}
      />
    ) : (
      <FormattedMessage id="SearchMapInfoCard.price" values={{ priceValue: priceValueNode }} />
    );

    const classes = classNames(rootClassName || css.root, className);
    const priceLabelClasses = classNames(css.priceLabel, {
      [css.mapLabelActive]: isActive,
      [css.noPriceSetLabel]: !formattedPrice,
    });
    const caretClasses = classNames(css.caret, { [css.caretActive]: isActive });

    const ariaPriceValue = formattedPrice
      ? intl.formatMessage(
          { id: 'SearchMapPriceLabel.price' },
          { priceValue: getPriceDisplayValue(formattedPrice, isAuthenticated) }
        )
      : null;

    const ariaLabel = ariaPriceValue
      ? hasMultiplePriceVariants
        ? intl.formatMessage({ id: 'SearchMapInfoCard.priceStartingFrom' }, { priceValue: ariaPriceValue })
        : intl.formatMessage({ id: 'SearchMapInfoCard.price' }, { priceValue: ariaPriceValue })
      : intl.formatMessage({ id: 'SearchMapPriceLabel.screenreader.mapMarkerWithoutPrice' });

    return (
      <button
        className={classes}
        onClick={() => onListingClicked(currentListing)}
        aria-label={ariaLabel}
      >
        <div className={css.caretShadow} />
        <div className={priceLabelClasses}>{priceMessage}</div>
        <div className={caretClasses} />
      </button>
    );
  }
}

const mapStateToProps = state => ({
  isAuthenticated: isAuthenticatedSelector(state),
});

export default compose(connect(mapStateToProps), injectIntl)(SearchMapPriceLabel);
